/**
 * useAchievements - Hook for badge/achievement system
 */

import { useState, useEffect, useCallback } from 'react';
import { achievementService } from '../services/AchievementService';

export function useAchievements() {
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const refreshBadges = useCallback(() => {
    setBadges(achievementService.getAllBadges());
    setStats(achievementService.getStats());
  }, []);

  useEffect(() => {
    refreshBadges();
  }, [refreshBadges]);

  // Check for new badge notifications periodically
  useEffect(() => {
    const checkNotifications = () => {
      if (achievementService.hasPendingNotifications()) {
        const pending = achievementService.getPendingNotifications();
        setNotifications(prev => [...prev, ...pending]);
      }
    };

    const interval = setInterval(checkNotifications, 1000);
    return () => clearInterval(interval);
  }, []);

  const checkBadges = useCallback(() => {
    const newlyUnlocked = achievementService.checkAndUnlockBadges();
    if (newlyUnlocked.length > 0) {
      refreshBadges();
    }
    return newlyUnlocked;
  }, [refreshBadges]);

  const dismissNotification = useCallback((index) => {
    setNotifications(prev => prev.filter((_, i) => i !== index));
  }, []);

  const dismissAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const getBadgeProgress = useCallback((badgeId) => {
    return achievementService.getBadgeProgress(badgeId);
  }, []);

  return {
    badges,
    stats,
    notifications,
    
    // Actions
    checkBadges,
    refreshBadges,
    dismissNotification,
    dismissAllNotifications,
    getBadgeProgress,
    
    // Quick accessors
    unlockedBadges: badges.filter(b => b.unlocked),
    lockedBadges: badges.filter(b => !b.unlocked),
    totalBadges: stats?.total || 0,
    unlockedCount: stats?.unlocked || 0,
    percentage: stats?.percentage || 0,
    hasNotifications: notifications.length > 0
  };
}

export default useAchievements;